import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";

import { useCreateIndicator, useCreateThematicArea, useGetAllThematicAreas, useGetIndicatorsByThematicAndYear, useUpdateIndicator, useUpdateThematicArea } from "service/dpat.service";



const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const DEFAULT_THEMATIC_AREA_FORM = {
  thematicAreaName: "",
  thematicAreaDescription: "",
  thematicAreaCode: "",
  status: "ACTIVE",
};

const DEFAULT_INDICATOR_FORM = {
  indicatorName: "",
  thematicAreas: "",
  assessmentGuide: "",
  scoringCriteria: "",
  meansOfVerification: "",
  maxScore: 0,
  code: "",
  status: "ACTIVE",
};

function normalizeStatus(value) {
  return String(value || "").trim().toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";
}

function isActiveStatus(value) {
  return normalizeStatus(value) === "ACTIVE";
}

function formatStatusLabel(value) {
  return normalizeStatus(value) === "ACTIVE" ? "Active" : "Inactive";
}

function getThematicAreaDisplayCode(thematicArea, index) {
  return thematicArea.thematicAreaCode || `TA ${index + 1}`;
}

function RowActionMenu({ isOpen, onToggle, items }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || items.length * 36 + 10;
    const menuWidth = menuRef.current?.offsetWidth || 144;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpwards = spaceBelow < menuHeight + 8;

    const top = openUpwards
      ? Math.max(8, triggerRect.top - menuHeight - 6)
      : Math.min(window.innerHeight - menuHeight - 8, triggerRect.bottom + 6);
    const left = Math.min(
      Math.max(8, triggerRect.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    );

    setMenuPosition({ top, left });
  }, [items.length]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const frame = requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div className="inline-flex" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Open actions menu"
      >
        <Icon name="MoreHorizontal" className="h-4 w-4" />
      </button>

      {isOpen
        ? createPortal(
          <div
            ref={menuRef}
            style={{ top: menuPosition.top, left: menuPosition.left }}
            className="fixed z-[1500] min-w-[9rem] rounded-md border border-border bg-card p-1 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  item.onClick();
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition-colors ${item.tone === "destructive"
                    ? "text-destructive hover:bg-destructive/10"
                    : "text-foreground hover:bg-muted"
                  }`}
              >
                <Icon name={item.icon} className="h-3.5 w-3.5" />
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function CatalogModal({
  isOpen,
  title,
  description,
  onClose,
  onSubmit,
  children,
  onReset,
  error,
  submitLabel,
  isLoading = false
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-border bg-card shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icon name="X" className="h-4 w-4" />
              </button>
            </div>

            <form className="space-y-4 px-6 py-5 max-h-[70vh] overflow-y-auto" onSubmit={onSubmit}>
              {children}

              {error ? (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
                <Button type="button" variant="outline" onClick={onReset} disabled={isLoading}>
                  Reset Form
                </Button>
                <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  <Icon name={isLoading ? "Loader2" : "Save"} className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  {isLoading ? "Saving..." : submitLabel}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function DpatIndicatorsSetupWorkspace({ activeModule }) {
  const { user } = useAuth();

  // State declarations
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedThematicAreaId, setSelectedThematicAreaId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [thematicAreaForm, setThematicAreaForm] = useState(DEFAULT_THEMATIC_AREA_FORM);
  const [thematicAreaError, setThematicAreaError] = useState("");
  const [editingThematicAreaId, setEditingThematicAreaId] = useState(null);
  const [isThematicAreaModalOpen, setIsThematicAreaModalOpen] = useState(false);
  const [openThematicAreaActionId, setOpenThematicAreaActionId] = useState(null);
  const [indicatorsList, setIndicatorsList] = useState([]);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [indicatorForm, setIndicatorForm] = useState(DEFAULT_INDICATOR_FORM);
  const [indicatorError, setIndicatorError] = useState("");
  const [savingIndicatorId, setSavingIndicatorId] = useState(null);
  const [expandedIndicatorId, setExpandedIndicatorId] = useState(null);

  // React Query hooks for Thematic Area
  const { data: thematicAreas, isLoading: isLoadingThematicAreas } = useGetAllThematicAreas();
  const createThematicArea = useCreateThematicArea();
  const updateThematicArea = useUpdateThematicArea();

  // React Query hooks for Indicators - now with year filter
  const {
    data: indicators,
    refetch: refetchIndicators,
    isLoading: isLoadingIndicators
  } = useGetIndicatorsByThematicAndYear(selectedThematicAreaId, selectedYear);

  const createIndicator = useCreateIndicator();
  const updateIndicator = useUpdateIndicator();

  // Effect: Set default selected Thematic Area when data loads
  useEffect(() => {
    if (thematicAreas?.data && thematicAreas.data.length > 0 && !selectedThematicAreaId) {
      setSelectedThematicAreaId(thematicAreas.data[0].thematicAreaId);
    }
  }, [thematicAreas, selectedThematicAreaId]);

  // Effect: Refetch Indicators when selected Thematic Area or Year changes
  useEffect(() => {
    if (selectedThematicAreaId && selectedYear) {
      refetchIndicators();
    }
  }, [selectedThematicAreaId, selectedYear, refetchIndicators]);

  // Effect: Process Indicators data
  useEffect(() => {
    if (indicators?.data) {
      setIndicatorsList(indicators.data);
    } else {
      setIndicatorsList([]);
    }
  }, [indicators]);

  // Filtered Thematic Areas
  const filteredThematicAreas = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return thematicAreas?.data || [];
    return (thematicAreas?.data || []).filter((ta) =>
      [ta.thematicAreaCode, ta.thematicAreaName, ta.thematicAreaDescription, formatStatusLabel(ta.status)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [thematicAreas, searchTerm]);

  // Selected Thematic Area
  const selectedThematicArea = useMemo(
    () => (thematicAreas?.data || []).find((ta) => ta.thematicAreaId === selectedThematicAreaId) || null,
    [thematicAreas, selectedThematicAreaId],
  );

  // Active Thematic Area count
  const activeThematicAreaCount = useMemo(
    () => (thematicAreas?.data || []).filter((ta) => isActiveStatus(ta.status)).length,
    [thematicAreas],
  );

  // Available years for selection (current year ± 10 years)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
      years.push(i.toString());
    }
    return years;
  }, []);

  // Reset functions
  const resetThematicAreaForm = () => {
    setThematicAreaForm(DEFAULT_THEMATIC_AREA_FORM);
    setThematicAreaError("");
  };

  const resetIndicatorForm = () => {
    setIndicatorForm(DEFAULT_INDICATOR_FORM);
    setIndicatorError("");
  };

  // Close modal functions
  const closeThematicAreaModal = () => {
    resetThematicAreaForm();
    setEditingThematicAreaId(null);
    setIsThematicAreaModalOpen(false);
    setOpenThematicAreaActionId(null);
  };

  const closeIndicatorModal = () => {
    resetIndicatorForm();
    setEditingIndicator(null);
    setIsIndicatorModalOpen(false);
  };

  // Open modal functions
  const openAddThematicAreaModal = () => {
    resetThematicAreaForm();
    setEditingThematicAreaId(null);
    setIsThematicAreaModalOpen(true);
  };

  const openEditThematicAreaModal = (thematicArea) => {
    if (!thematicArea) return;
    setOpenThematicAreaActionId(null);
    setThematicAreaForm({
      thematicAreaName: thematicArea.thematicAreaName || "",
      thematicAreaDescription: thematicArea.thematicAreaDescription || "",
      thematicAreaCode: thematicArea.thematicAreaCode || "",
      status: normalizeStatus(thematicArea.status),
    });
    setThematicAreaError("");
    setEditingThematicAreaId(thematicArea.thematicAreaId);
    setIsThematicAreaModalOpen(true);
  };

  const openAddIndicatorModal = () => {
    resetIndicatorForm();
    setEditingIndicator(null);
    setIsIndicatorModalOpen(true);
  };

  const openEditIndicatorModal = (indicator) => {
    setIndicatorForm({
      indicatorName: indicator.indicatorName || "",
      thematicAreas: indicator.thematicAreas || "",
      assessmentGuide: indicator.assessmentGuide || "",
      scoringCriteria: indicator.scoringCriteria || "",
      meansOfVerification: indicator.meansOfVerification || "",
      maxScore: indicator.maxScore || 0,
      code: indicator.code || "",
      status: indicator.status || "ACTIVE",
    });
    setEditingIndicator(indicator);
    setIsIndicatorModalOpen(true);
  };

  // Submit handlers
  const handleThematicAreaSubmit = async (event) => {
    event.preventDefault();
    if (!thematicAreaForm.thematicAreaName.trim()) {
      setThematicAreaError("Section name is required.");
      return;
    }
    if (!thematicAreaForm.thematicAreaCode.trim()) {
      setThematicAreaError("Section code is required.");
      return;
    }

    const thematicAreaData = {
      thematicAreaName: thematicAreaForm.thematicAreaName.trim(),
      thematicAreaDescription: thematicAreaForm.thematicAreaDescription.trim(),
      thematicAreaCode: thematicAreaForm.thematicAreaCode.trim().toUpperCase(),
      status: thematicAreaForm.status,
      createdBy: user?.user?.username || "system",
    };

    try {
      if (editingThematicAreaId) {
        await updateThematicArea.mutateAsync({ id: editingThematicAreaId, payload: thematicAreaData });
      } else {
        await createThematicArea.mutateAsync(thematicAreaData);
      }
      closeThematicAreaModal();
    } catch (error) {
      setThematicAreaError(error?.response?.data?.message || "Error saving Thematic Area");
    }
  };

  const handleIndicatorSubmit = async (event) => {
    event.preventDefault();
    if (!indicatorForm.indicatorName.trim()) {
      setIndicatorError("Indicator name is required.");
      return;
    }

    const indicatorData = {
      thematicAreaId: selectedThematicAreaId,
      year: selectedYear,
      indicatorName: indicatorForm.indicatorName.trim(),
      thematicAreas: indicatorForm.thematicAreas.trim(),
      assessmentGuide: indicatorForm.assessmentGuide.trim(),
      scoringCriteria: indicatorForm.scoringCriteria,
      meansOfVerification: indicatorForm.meansOfVerification,
      maxScore: Number(indicatorForm.maxScore),
      code: indicatorForm.code?.trim().toUpperCase() || "",
      status: indicatorForm.status,
      createdBy: user?.user?.username || "system",
    };

    try {
      setSavingIndicatorId(editingIndicator?.indicatorId || 'new');
      if (editingIndicator) {
        await updateIndicator.mutateAsync({ id: editingIndicator.indicatorId, payload: indicatorData });
      } else {
        await createIndicator.mutateAsync(indicatorData);
      }
      closeIndicatorModal();
    } catch (error) {
      setIndicatorError(error?.response?.data?.message || "Error saving Indicator");
    } finally {
      setSavingIndicatorId(null);
    }
  };

  const handleDeleteIndicator = async (indicatorId) => {
    if (window.confirm("Are you sure you want to delete this Indicator?")) {
      // Note: Delete API endpoint would need to be added to the service
      alert(`Delete Indicator with ID: ${indicatorId}`);
    }
  };

  const handleRemoveThematicArea = async (thematicAreaId) => {
    setOpenThematicAreaActionId(null);
    if (window.confirm("Are you sure you want to delete this Thematic Area? This will also delete all associated indicators.")) {
      alert(`Delete Thematic Area with ID: ${thematicAreaId}`);
    }
  };

  const toggleExpandIndicator = (indicatorId) => {
    setExpandedIndicatorId(expandedIndicatorId === indicatorId ? null : indicatorId);
  };

  if (isLoadingThematicAreas) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading Indicators Sections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Thematic Area Modal */}
      <CatalogModal
        isOpen={isThematicAreaModalOpen}
        title={editingThematicAreaId ? "Edit DPAT Section" : "Add DPAT Section"}
        description="Maintain the Section groups that define the DPAT structure for development planning."
        onClose={closeThematicAreaModal}
        onSubmit={handleThematicAreaSubmit}
        onReset={resetThematicAreaForm}
        error={thematicAreaError}
        submitLabel={editingThematicAreaId ? "Update Section" : "Save Section"}
        isLoading={createThematicArea.isLoading || updateThematicArea.isLoading}
      >
        <Input
          label="Section Name"
          placeholder="e.g., Compliance, Service Delivery, ..."
          value={thematicAreaForm.thematicAreaName}
          onChange={(event) => setThematicAreaForm((current) => ({ ...current, thematicAreaName: event.target.value }))}
        />
        <Input
          label="Section Code"
          placeholder="e.g., GOV-001"
          value={thematicAreaForm.thematicAreaCode}
          onChange={(event) => setThematicAreaForm((current) => ({ ...current, thematicAreaCode: event.target.value.toUpperCase() }))}
        />
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={STATUS_OPTIONS.find((option) => option.value === thematicAreaForm.status) || null}
          onChange={(option) => setThematicAreaForm((current) => ({ ...current, status: option?.value || "ACTIVE" }))}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Section Description</label>
          <textarea
            rows={4}
            value={thematicAreaForm.thematicAreaDescription}
            onChange={(event) => setThematicAreaForm((current) => ({ ...current, thematicAreaDescription: event.target.value }))}
            placeholder="Describe the scope and focus areas of this section"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
          />
        </div>
      </CatalogModal>

      {/* Indicator Modal with new fields */}
      <CatalogModal
        isOpen={isIndicatorModalOpen}
        title={editingIndicator ? "Edit DPAT Indicator" : "Add New DPAT Indicator"}
        description={`Configure indicator for ${selectedThematicArea?.thematicAreaName || "selected Thematic Area"} - Year ${selectedYear}`}
        onClose={closeIndicatorModal}
        onSubmit={handleIndicatorSubmit}
        onReset={resetIndicatorForm}
        error={indicatorError}
        submitLabel={editingIndicator ? "Update Indicator" : "Add Indicator"}
        isLoading={createIndicator.isLoading || updateIndicator.isLoading}
      >
        <Input
          label="Indicator Name"
          placeholder="e.g., Number of district planning committees established"
          value={indicatorForm.indicatorName}
          required
          onChange={(event) => {
            setIndicatorError("");
            setIndicatorForm((current) => ({ ...current, indicatorName: event.target.value }));
          }}
        />
        
        <Input
          label="Indicator Code"
          placeholder="e.g., GOV-IND-001"
          value={indicatorForm.code}
          onChange={(event) => setIndicatorForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))}
        />
        
        <Input
          label="Thematic Areas"
          placeholder="e.g., Governance, Health, Education"
          value={indicatorForm.thematicAreas}
          onChange={(event) => setIndicatorForm((current) => ({ ...current, thematicAreas: event.target.value }))}
        />
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Assessment Guide</label>
          <textarea
            rows={3}
            value={indicatorForm.assessmentGuide}
            onChange={(event) => setIndicatorForm((current) => ({ ...current, assessmentGuide: event.target.value }))}
            placeholder="Detailed guide on how to assess this indicator. Example: Review committee meeting minutes and attendance records to verify committee establishment and functionality."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Scoring Criteria</label>
          <textarea
            rows={3}
            value={indicatorForm.scoringCriteria}
            onChange={(event) => setIndicatorForm((current) => ({ ...current, scoringCriteria: event.target.value }))}
            placeholder="Define how this indicator will be scored. Example: 1 point per committee established, max 10 points"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Means of Verification</label>
          <textarea
            rows={2}
            value={indicatorForm.meansOfVerification}
            onChange={(event) => setIndicatorForm((current) => ({ ...current, meansOfVerification: event.target.value }))}
            placeholder="How will this indicator be verified? (e.g., Committee meeting minutes, attendance records)"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Max Score"
            type="number"
            step="1"
            placeholder="100"
            value={indicatorForm.maxScore}
            required
            onChange={(event) => setIndicatorForm((current) => ({ ...current, maxScore: parseFloat(event.target.value) || 0 }))}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((option) => option.value === indicatorForm.status) || null}
            onChange={(option) => setIndicatorForm((current) => ({ ...current, status: option?.value || "ACTIVE" }))}
          />
        </div>
      </CatalogModal>

      {/* Header Section */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Icon name={activeModule?.icon || "Layout"} className="h-3.5 w-3.5" />
              DPAT Setup Workspace
            </div>
            <h2 className="text-2xl font-semibold text-foreground">{activeModule?.label || "DPAT Indicators Setup"}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{activeModule?.summary || "Configure Thematic Areas and their corresponding indicators for the District Performance Assessment Tool."}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              First create Section, then select a year to configure indicators with assessment guides, scoring criteria, and verification methods.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Total Sections</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{thematicAreas?.data?.length || 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Active Sections</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{activeThematicAreaCount}</p>
              </div>
            </div>

            <Button type="button" onClick={openAddThematicAreaModal}>
              <Icon name="Plus" className="h-4 w-4" />
              Add Section
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        {/* Thematic Area Table */}
        <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <Icon name="Layers" className="h-3.5 w-3.5" />
              DPAT Indicators Section
            </div>
            <h3 className="mt-3 text-lg font-semibold text-foreground">Indicators Configuration</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Select a Section to configure its indicators for a specific year.
            </p>
          </div>

          <div className="mb-4 relative">
            <label className="sr-only" htmlFor="dpat-ta-search">Search Section Indicator</label>
            <Icon
              name="Search"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="dpat-ta-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by code, name, description, or status"
              className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Section</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {filteredThematicAreas.map((thematicArea, index) => {
                  const isSelected = selectedThematicArea?.thematicAreaId === thematicArea.thematicAreaId;
                  return (
                    <tr
                      key={thematicArea.thematicAreaId}
                      className={`cursor-pointer transition-colors ${isSelected ? "bg-muted" : "hover:bg-muted/30"
                        }`}
                      onClick={() => setSelectedThematicAreaId(thematicArea.thematicAreaId)}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {getThematicAreaDisplayCode(thematicArea, index)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{thematicArea.thematicAreaName}</p>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {thematicArea.thematicAreaDescription}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${isActiveStatus(thematicArea.status)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {formatStatusLabel(thematicArea.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <RowActionMenu
                            isOpen={openThematicAreaActionId === thematicArea.thematicAreaId}
                            onToggle={() =>
                              setOpenThematicAreaActionId((current) => (current === thematicArea.thematicAreaId ? null : thematicArea.thematicAreaId))
                            }
                            items={[
                              {
                                key: "edit",
                                label: "Edit",
                                icon: "Edit",
                                onClick: () => openEditThematicAreaModal(thematicArea),
                              },
                              {
                                key: "remove",
                                label: "Remove",
                                icon: "Trash2",
                                tone: "destructive",
                                onClick: () => handleRemoveThematicArea(thematicArea.thematicAreaId),
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredThematicAreas.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {searchTerm ? "No Sections match the current search." : "No Sections added yet. Click 'Add Section' to create one."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>

        {/* Indicators Configuration Panel with expandable rows */}
        {selectedThematicArea ? (
          <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    <Icon name="Target" className="h-3.5 w-3.5" />
                    Indicator Management
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-foreground">{selectedThematicArea.thematicAreaName}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Code: {selectedThematicArea.thematicAreaCode}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Create and manage indicators for specific years under this section
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-foreground">Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground"
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <Button type="button" size="sm" onClick={openAddIndicatorModal}>
                    <Icon name="Plus" className="h-3.5 w-3.5" />
                    Add Indicator
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-card">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground w-8"></th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Indicator Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Thematic Areas</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Max Score</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoadingIndicators ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          <div className="flex items-center justify-center gap-2">
                            <Icon name="Loader2" className="h-4 w-4 animate-spin" />
                            Loading Indicators...
                          </div>
                        </td>
                      </tr>
                    ) : indicatorsList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          No indicators configured for {selectedYear}. Click "Add Indicator" to create one.
                        </td>
                      </tr>
                    ) : (
                      indicatorsList.map((indicator) => (
                        <React.Fragment key={indicator.indicatorId}>
                          <tr className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleExpandIndicator(indicator.indicatorId)}
                                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <Icon 
                                  name={expandedIndicatorId === indicator.indicatorId ? "ChevronUp" : "ChevronDown"} 
                                  className="h-4 w-4" 
                                />
                              </button>
                             </td>
                            <td className="px-4 py-3 font-mono text-xs text-foreground">
                              {indicator.code || "—"}
                             </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-foreground">{indicator.indicatorName}</p>
                             </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {indicator.thematicAreas || "—"}
                             </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {indicator.maxScore}
                             </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${indicator.status === "ACTIVE"
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                                }`}>
                                {indicator.status}
                              </span>
                             </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => openEditIndicatorModal(indicator)}
                                  className="p-1 text-muted-foreground hover:text-foreground"
                                  title="Edit Indicator"
                                >
                                  <Icon name="Edit" className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteIndicator(indicator.indicatorId)}
                                  className="p-1 text-muted-foreground hover:text-destructive"
                                  title="Delete Indicator"
                                >
                                  <Icon name="Trash2" className="h-4 w-4" />
                                </button>
                              </div>
                             </td>
                           </tr>
                          {expandedIndicatorId === indicator.indicatorId && (
                            <tr className="bg-muted/20">
                              <td colSpan={7} className="px-4 py-4">
                                <div className="space-y-3">
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-2">Assessment Guide</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {indicator.assessmentGuide || "No assessment guide provided."}
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-2">Scoring Criteria</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {indicator.scoringCriteria || "No scoring criteria provided."}
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-2">Means of Verification</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {indicator.meansOfVerification || "No means of verification provided."}
                                    </p>
                                  </div>
                                </div>
                               </td>
                             </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Info Note */}
              <div className="rounded-lg bg-blue-50 p-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Icon name="Info" className="h-4 w-4 text-blue-600" />
                  <span className="text-xs text-blue-800">
                    <strong>Note:</strong> Each indicator includes assessment guides, scoring criteria, and means of verification.
                    Click the chevron icon to expand and view detailed information for each indicator.
                  </span>
                </div>
              </div>

              {/* Thematic Area Info */}
              <div className="rounded-lg bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Icon name="Layers" className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Thematic Area: <strong>{selectedThematicArea.thematicAreaName}</strong> ({selectedThematicArea.thematicAreaCode}) -
                    Status: <strong>{formatStatusLabel(selectedThematicArea.status)}</strong>
                  </span>
                </div>
              </div>
            </div>
          </article>
        ) : (
          <article className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Icon name="Layers" className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">No Thematic Area Selected</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-md">
                Please select a Thematic Area from the left panel to configure its indicators for different years.
              </p>
            </div>
          </article>
        )}
      </section>
    </div>
  );
}